export enum ProposalTrackingStatus {
  /** Frontend-only. Tracking data has never been initialized for this opportunity. */
  Untracked = -1,
  /** Tracking initialized. No proposal revision has been generated yet. */
  NotGenerated = 0,
  DraftUpToDate = 1,
  DraftOutOfDate = 2,
  SentUnviewedUpToDate = 3,
  SentUnviewedOutOfDate = 4,
  ClientViewedUpToDate = 5,
  ClientViewedOutOfDate = 6
}

export interface ProposalTrackingData {
  status: ProposalTrackingStatus;
  latestRevisionId?: number | null;
  latestRevisionDate?: string | null;
  isSent: boolean;
  sentAt?: string | null;
  sentBy?: string | null;
  isOutOfDate: boolean;
  lastContentModifiedAt?: string | null;
  clientViewCount: number;
  firstClientViewedAt?: string | null;
  lastClientViewedAt?: string | null;
  lastViewedRevisionId?: number | null;
  internalPreviewCount: number;
  lastInternalPreviewAt?: string | null;
}

export interface ProposalTrackingBadgeInfo {
  status: ProposalTrackingStatus;
  label: string;
  subLabel: string;
  icon: string;
  iconColor: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  btnViewColor: string;
  btnGenColor: string;
  isStale: boolean;
}

/** Badge for the copy-link button: "Has client received and read the proposal?" */
export interface ProposalClientBadge {
  icon: string;
  color: string;
  label: string;
  tooltip: string;
}

/** Badge for the gen button: "Is the proposal generated and current?" */
export interface ProposalRevisionBadge {
  icon: string;
  color: string;
  label: string;
  tooltip: string;
}

export class ProposalTrackingHelper {
  public static parse(data: any): ProposalTrackingData {
    if (!data) {
      return this.defaultTrackingData();
    }

    // If string, parse JSON
    let parsed = data;
    if (typeof data === 'string') {
      try {
        parsed = JSON.parse(data);
      } catch {
        return this.defaultTrackingData();
      }
    }

    // Check nested trackingData property on profileAdditionDetail or entity
    if (parsed.trackingData) {
      return this.parse(parsed.trackingData);
    }
    if (parsed.trackingDataJson) {
      return this.parse(parsed.trackingDataJson);
    }

    // If the object does not contain tracking fields (e.g., an uninitialized profileAdditionDetail),
    // treat it as Untracked rather than defaulting to NotGenerated.
    const hasTrackingPayload =
      (parsed.status !== undefined && parsed.status !== null) ||
      (parsed.Status !== undefined && parsed.Status !== null) ||
      (parsed.latestRevisionId !== undefined && parsed.latestRevisionId !== null) ||
      (parsed.LatestRevisionId !== undefined && parsed.LatestRevisionId !== null) ||
      (parsed.clientViewCount !== undefined && parsed.clientViewCount !== null) ||
      (parsed.ClientViewCount !== undefined && parsed.ClientViewCount !== null);

    if (!hasTrackingPayload) {
      return this.defaultTrackingData();
    }

    const latestRevisionId = parsed.latestRevisionId ?? parsed.LatestRevisionId ?? null;
    const isSent = Boolean(parsed.isSent ?? parsed.IsSent ?? false);
    const isOutOfDate = Boolean(parsed.isOutOfDate ?? parsed.IsOutOfDate ?? false);
    const clientViewCount = Number(parsed.clientViewCount ?? parsed.ClientViewCount ?? 0);

    let status = parsed.status ?? parsed.Status;
    if (status === undefined || status === null) {
      if (!latestRevisionId || latestRevisionId <= 0) {
        status = ProposalTrackingStatus.NotGenerated;
      } else if (!isSent) {
        status = isOutOfDate ? ProposalTrackingStatus.DraftOutOfDate : ProposalTrackingStatus.DraftUpToDate;
      } else if (clientViewCount === 0) {
        status = isOutOfDate ? ProposalTrackingStatus.SentUnviewedOutOfDate : ProposalTrackingStatus.SentUnviewedUpToDate;
      } else {
        status = isOutOfDate ? ProposalTrackingStatus.ClientViewedOutOfDate : ProposalTrackingStatus.ClientViewedUpToDate;
      }
    }

    return {
      status: Number(status) as ProposalTrackingStatus,
      latestRevisionId: latestRevisionId,
      latestRevisionDate: parsed.latestRevisionDate ?? parsed.LatestRevisionDate ?? null,
      isSent: isSent,
      sentAt: parsed.sentAt ?? parsed.SentAt ?? null,
      sentBy: parsed.sentBy ?? parsed.SentBy ?? null,
      isOutOfDate: isOutOfDate,
      lastContentModifiedAt: parsed.lastContentModifiedAt ?? parsed.LastContentModifiedAt ?? null,
      clientViewCount: clientViewCount,
      firstClientViewedAt: parsed.firstClientViewedAt ?? parsed.FirstClientViewedAt ?? null,
      lastClientViewedAt: parsed.lastClientViewedAt ?? parsed.LastClientViewedAt ?? null,
      lastViewedRevisionId: parsed.lastViewedRevisionId ?? parsed.LastViewedRevisionId ?? null,
      internalPreviewCount: Number(parsed.internalPreviewCount ?? parsed.InternalPreviewCount ?? 0),
      lastInternalPreviewAt: parsed.lastInternalPreviewAt ?? parsed.LastInternalPreviewAt ?? null
    };
  }

  public static defaultTrackingData(): ProposalTrackingData {
    return {
      status: ProposalTrackingStatus.Untracked,
      latestRevisionId: null,
      latestRevisionDate: null,
      isSent: false,
      sentAt: null,
      sentBy: null,
      isOutOfDate: false,
      lastContentModifiedAt: null,
      clientViewCount: 0,
      firstClientViewedAt: null,
      lastClientViewedAt: null,
      lastViewedRevisionId: null,
      internalPreviewCount: 0,
      lastInternalPreviewAt: null
    };
  }

  public static getBadgeInfo(tracking: ProposalTrackingData): ProposalTrackingBadgeInfo {
    switch (tracking.status) {
      case ProposalTrackingStatus.DraftUpToDate:
        return {
          status: ProposalTrackingStatus.DraftUpToDate,
          label: 'Draft',
          subLabel: 'Up-to-Date',
          icon: 'edit_note',
          iconColor: '#f59e0b', // Amber-500
          badgeBg: 'rgba(245, 158, 11, 0.1)',
          badgeBorder: 'rgba(245, 158, 11, 0.3)',
          badgeText: '#f59e0b',
          btnViewColor: '#ef4444',
          btnGenColor: '#10b981',
          isStale: false
        };

      case ProposalTrackingStatus.DraftOutOfDate:
        return {
          status: ProposalTrackingStatus.DraftOutOfDate,
          label: 'Draft',
          subLabel: 'Stale (Regenerate)',
          icon: 'warning_amber',
          iconColor: '#f97316', // Orange-500
          badgeBg: 'rgba(249, 115, 22, 0.1)',
          badgeBorder: 'rgba(249, 115, 22, 0.4)',
          badgeText: '#f97316',
          btnViewColor: '#ef4444',
          btnGenColor: '#f97316',
          isStale: true
        };

      case ProposalTrackingStatus.SentUnviewedUpToDate:
        return {
          status: ProposalTrackingStatus.SentUnviewedUpToDate,
          label: 'Sent',
          subLabel: 'Pending View',
          icon: 'send',
          iconColor: '#0284c7', // Sky-600
          badgeBg: 'rgba(2, 132, 199, 0.1)',
          badgeBorder: 'rgba(2, 132, 199, 0.3)',
          badgeText: '#0284c7',
          btnViewColor: '#0284c7',
          btnGenColor: '#10b981',
          isStale: false
        };

      case ProposalTrackingStatus.SentUnviewedOutOfDate:
        return {
          status: ProposalTrackingStatus.SentUnviewedOutOfDate,
          label: 'Sent',
          subLabel: 'Stale (Regenerate)',
          icon: 'send',
          iconColor: '#f97316', // Orange-500
          badgeBg: 'rgba(249, 115, 22, 0.1)',
          badgeBorder: 'rgba(249, 115, 22, 0.4)',
          badgeText: '#f97316',
          btnViewColor: '#f97316',
          btnGenColor: '#f97316',
          isStale: true
        };

      case ProposalTrackingStatus.ClientViewedUpToDate:
        return {
          status: ProposalTrackingStatus.ClientViewedUpToDate,
          label: 'Viewed',
          subLabel: `${tracking.clientViewCount} views`,
          icon: 'visibility',
          iconColor: '#10b981', // Emerald-500
          badgeBg: 'rgba(16, 185, 129, 0.12)',
          badgeBorder: 'rgba(16, 185, 129, 0.35)',
          badgeText: '#10b981',
          btnViewColor: '#10b981',
          btnGenColor: '#10b981',
          isStale: false
        };

      case ProposalTrackingStatus.ClientViewedOutOfDate:
        return {
          status: ProposalTrackingStatus.ClientViewedOutOfDate,
          label: 'Viewed',
          subLabel: 'Newer revision unseen',
          icon: 'visibility',
          iconColor: '#f97316', // Orange-500
          badgeBg: 'rgba(249, 115, 22, 0.12)',
          badgeBorder: 'rgba(249, 115, 22, 0.45)',
          badgeText: '#f97316',
          btnViewColor: '#10b981',
          btnGenColor: '#f97316',
          isStale: true
        };

      case ProposalTrackingStatus.Untracked:
        return {
          status: ProposalTrackingStatus.Untracked,
          label: 'Untracked',
          subLabel: 'Pending init',
          icon: 'hourglass_empty',
          iconColor: '#94a3b8',
          badgeBg: 'rgba(148, 163, 184, 0.08)',
          badgeBorder: 'rgba(148, 163, 184, 0.2)',
          badgeText: '#94a3b8',
          btnViewColor: '#94a3b8',
          btnGenColor: '#64748b',
          isStale: false
        };

      case ProposalTrackingStatus.NotGenerated:
      default:
        return {
          status: ProposalTrackingStatus.NotGenerated,
          label: 'No Proposal',
          subLabel: 'Action required',
          icon: 'error_outline',
          iconColor: '#ef4444', // Red-500
          badgeBg: 'rgba(239, 68, 68, 0.1)',
          badgeBorder: 'rgba(239, 68, 68, 0.35)',
          badgeText: '#ef4444',
          btnViewColor: '#ef4444',
          btnGenColor: '#ef4444',
          isStale: true
        };
    }
  }

  /**
   * Client-side badge for the copy-link button.
   * Answers: "Has client received and read the proposal?"
   */
  public static getClientBadge(tracking: ProposalTrackingData): ProposalClientBadge {
    if (tracking.status === ProposalTrackingStatus.Untracked) {
      return { icon: 'link_off', color: '#94a3b8', label: 'Untracked', tooltip: 'Tracking not yet initialized' };
    }
    if (!tracking.latestRevisionId) {
      return { icon: 'link_off', color: '#ef4444', label: 'No proposal', tooltip: 'No proposal generated yet — client cannot view anything' };
    }
    if (!tracking.isSent) {
      return { icon: 'link_off', color: '#ef4444', label: 'Not sent', tooltip: 'Link not sent to client yet — client has no access' };
    }
    if (tracking.clientViewCount === 0) {
      return { icon: 'mail', color: '#0284c7', label: 'Sent', tooltip: 'Sent — client has not opened it yet' };
    }

    const viewedLatest = !tracking.lastViewedRevisionId
      || !tracking.latestRevisionId
      || tracking.lastViewedRevisionId >= tracking.latestRevisionId;

    if (viewedLatest) {
      return { icon: 'visibility', color: '#10b981', label: 'Viewed', tooltip: `Viewed ${tracking.clientViewCount}x — client saw the latest revision` };
    }
    return { icon: 'visibility', color: '#f97316', label: 'Viewed', tooltip: `Viewed Rev #${tracking.lastViewedRevisionId} — has not seen Rev #${tracking.latestRevisionId}` };
  }

  /**
   * Proposal-side badge for the generate button.
   * Answers: "Has the proposal been generated? Is it still current?"
   */
  public static getProposalBadge(tracking: ProposalTrackingData): ProposalRevisionBadge {
    if (tracking.status === ProposalTrackingStatus.Untracked) {
      return { icon: 'hourglass_empty', color: '#94a3b8', label: 'Untracked', tooltip: 'Tracking not yet initialized' };
    }
    if (!tracking.latestRevisionId) {
      return { icon: 'error_outline', color: '#ef4444', label: 'No proposal', tooltip: 'No proposal generated — client has nothing to view' };
    }
    if (tracking.isOutOfDate) {
      return { icon: 'warning_amber', color: '#f97316', label: 'Outdated', tooltip: 'Pricing or content changed after last revision — regenerate' };
    }
    return { icon: 'check_circle', color: '#10b981', label: 'Up to date', tooltip: `Rev #${tracking.latestRevisionId} — up to date` };
  }

  public static maskUrl(url: string): string {
    if (!url) return '';
    // Mask UUID inside URL (e.g., .../proposal-views/7a1b7420-3004-45e7-a5fd-a67f0891c2dc -> .../proposal-views/7a1b••••••••••••••••••••••••••••c2dc)
    const parts = url.split('/proposal-views/');
    if (parts.length === 2) {
      const id = parts[1];
      if (id.length > 8) {
        const prefix = id.substring(0, 4);
        const suffix = id.substring(id.length - 4);
        return `${parts[0]}/proposal-views/${prefix}${'•'.repeat(24)}${suffix}`;
      }
    }
    return url;
  }
}
